import {
  type Dispatch,
  type ReactElement,
  type Ref,
  type SetStateAction,
} from 'react';
import { Pressable, Text, View } from 'react-native';
import type {
  SessionResult,
  WorkoutEnergyReadout,
} from '@jackedlogs/shared';
import { CaloriesEffortBlock } from '../index';

import { WorkoutTotalsCard } from './Totals';
import { workoutTotals } from './totalsMath';
import { WorkoutHeatMap } from './WorkoutHeatMap';
import { DateEditorCard } from './DateEditorCard';
import type { LoggerDispatch } from './state/loggerDraft';
import type { LoggerDraft, WeightUnit } from './state/loggerDraftTypes';
import type { makeStyles } from './loggerStyles';
import type { useTheme } from './theme';
import type { LoggerHostDeps } from './hostDeps';
import type { PostSessionSummaryHandle } from './usePostSaveSummary';

// Below-the-entry-list footer + post-save region. Extracted verbatim out
// of LoggerScreen (Track R). Renders, in order: the add-exercise button,
// workout totals card, body map, and date editor (only while logging,
// i.e. postSaveSummary === null) followed by the post-session summary
// card, post-save Share/Done actions, and the validation / server error
// boxes (shown per their own gates). Presentational: every action routes
// back through dispatch / the passed handlers.
type WorkoutMetaFooterProps = {
  postSaveSummary: { sessionId: string; onDone: () => void } | null;
  summarySessionId: string | null;
  sessionResultValue: SessionResult | null;
  /** Energy readout (calories + effort/HR): the live estimate while logging, or
   *  the STORED measured value when editing an enriched session (B2c). null when
   *  nothing to show. HC-8. */
  energyReadout?: WorkoutEnergyReadout | null;
  totals: ReturnType<typeof workoutTotals>;
  draft: LoggerDraft;
  error: string | null;
  serverError: string | null;
  // Timeline logger renders its OWN add-exercise button between the note
  // composer and the timeline feed (inbox bdfb84a3), so it suppresses the
  // footer's copy. The card logger leaves it false → button stays here.
  hideAddExercise?: boolean;
  headerSharing: boolean;
  triggerHeaderShare: () => void;
  dispatch: LoggerDispatch;
  defaultUnit: WeightUnit;
  locale: string;
  setPickerOpen: Dispatch<SetStateAction<boolean>>;
  summaryCardRef: Ref<PostSessionSummaryHandle>;
  styles: ReturnType<typeof makeStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
  PostSessionSummaryCard: LoggerHostDeps['PostSessionSummaryCard'];
  profile: import('./hostDeps').LoggerProfile;
};

export function WorkoutMetaFooter({
  postSaveSummary,
  summarySessionId,
  sessionResultValue,
  energyReadout,
  totals,
  draft,
  error,
  serverError,
  headerSharing,
  triggerHeaderShare,
  dispatch,
  defaultUnit,
  locale,
  setPickerOpen,
  summaryCardRef,
  hideAddExercise = false,
  styles,
  theme,
  t,
  PostSessionSummaryCard,
  profile,
}: WorkoutMetaFooterProps): ReactElement {
  return (
    <>
      {postSaveSummary === null ? (
        <>
          {hideAddExercise ? null : (
            <Pressable
              accessibilityRole="button"
              style={({ pressed }) => [
                styles.addExercise,
                pressed && styles.addExercisePressed,
              ]}
              onPress={() => setPickerOpen(true)}
            >
              <Text style={styles.addExerciseText}>
                {t('mobile.logger.addExercise')}
              </Text>
            </Pressable>
          )}
          {totals.sets > 0 ? (
            <WorkoutTotalsCard
              totals={totals}
              locale={locale}
              unit={defaultUnit}
              theme={theme}
              t={t}
            />
          ) : null}
          {totals.sets > 0 && energyReadout ? (
            // boxed: this block's siblings here (WorkoutTotalsCard,
            // WorkoutHeatMap below) are cards — DS-18 gives it the same
            // chrome instead of floating with only its internal divider.
            <CaloriesEffortBlock theme={theme} {...energyReadout} boxed />
          ) : null}
          {totals.sets > 0 ? (
            <WorkoutHeatMap byMuscle={totals.byMuscle} profile={profile} />
          ) : null}
          <DateEditorCard
            startedAt={draft.startedAt}
            endedAt={draft.endedAt}
            onSetStartedAt={(iso) =>
              dispatch({ type: 'set_started_at', startedAt: iso })
            }
            onSetEndedAt={(iso) =>
              dispatch({ type: 'set_ended_at', endedAt: iso })
            }
            locale={locale}
            theme={theme}
            t={t}
          />
        </>
      ) : null}
      {/* The VISIBLE post-session summary shows ONLY after a fresh save — NOT
          while editing a past session. Editing-of-past keeps the live logger UI
          (the draft totals above) per [[feedback_edit_mode_reuses_logger_ui]];
          showing the server SessionResult summary alongside the live draft was
          stacking two bodymaps + two "Work by muscle" panels on one screen
          (Antenore 2026-07-01).
          BUT the header share icon is actionable in edit too, and it drives
          capture through summaryCardRef. So in edit (postSaveSummary === null
          yet a past session's SessionResult is loaded → summarySessionId set,
          see useLoggerScreen:97-101) we still mount the card, but
          offscreenShareOnly: ONLY the 9:16 capture renderer + share() ref, no
          visible summary → the double-panel bug stays fixed AND share works
          (Antenore 2026-07-10). */}
      {summarySessionId !== null && sessionResultValue !== null ? (
        <PostSessionSummaryCard
          ref={summaryCardRef}
          result={sessionResultValue}
          energy={energyReadout ?? null}
          locale={locale}
          unit={defaultUnit}
          hideShareButton={postSaveSummary !== null}
          offscreenShareOnly={postSaveSummary === null}
        />
      ) : null}
      {postSaveSummary !== null && sessionResultValue !== null ? (
        <View style={styles.postSaveActions}>
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.postSaveShareBtn,
              pressed && styles.postSaveBtnPressed,
              headerSharing && styles.postSaveBtnDisabled,
            ]}
            onPress={() => void triggerHeaderShare()}
            disabled={headerSharing}
          >
            <Text style={styles.postSaveShareText}>
              {headerSharing
                ? t('mobile.postSession.sharing', {
                    defaultValue: 'Preparing…',
                  })
                : t('mobile.postSession.shareCta', {
                    defaultValue: 'Share session card',
                  })}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.postSaveDoneBtn,
              pressed && styles.postSaveBtnPressed,
            ]}
            onPress={() => postSaveSummary.onDone()}
          >
            <Text style={styles.postSaveDoneText}>
              {t('mobile.postSession.done', { defaultValue: 'Done' })}
            </Text>
          </Pressable>
        </View>
      ) : null}
      {error !== null ? (
        <Text style={styles.errorText}>
          {error.startsWith('mobile.logger.') ? t(error) : error}
        </Text>
      ) : null}
      {serverError !== null ? (
        <View style={styles.serverErrorBox}>
          <Text style={styles.serverErrorTitle}>
            {t('mobile.logger.serverErrorTitle')}
          </Text>
          <Text style={styles.serverErrorBody}>{serverError}</Text>
          <Text style={styles.serverErrorHint}>
            {t('mobile.logger.serverErrorHint')}
          </Text>
        </View>
      ) : null}
    </>
  );
}
